import * as React from 'react'

import styled from '@emotion/styled'

import { InformationOutlineIcon, NavigationCloseIcon } from '@arcade/web/icons/react'

import { breakpoints, spacing } from '../../theme'
import { SemanticColors } from '../../theme/arcadeColors'
import { addOpacity } from '../../theme/colors'
import ArcadeButton, { ButtonProps } from '../Button/ArcadeButton'
import { Flex } from '../layout/Flex'
import Stack from '../layout/Stack'

export type ArcadeStatusBannerVariant = 'prominent' | 'subtle' | 'error' | 'warning'

export interface ArcadeStatusBannerProps {
  /**
   * The variant determines the visual style and semantic meaning of the banner
   */
  variant: ArcadeStatusBannerVariant
  /**
   * Title of the banner
   */
  title: string
  /**
   * Whether to hide the title icon
   */
  hideTitleIcon?: boolean
  /**
   * Optional message content of the banner
   */
  message?: React.ReactNode
  /**
   * Optional icon to display above the title
   */
  headerIcon?: React.ReactNode
  /**
   * Callback when the close button is clicked. If provided, a close button will appear
   */
  onClose?: () => void
  /**
   * Optional ArcadeButton to display in the action area
   * Must be an ArcadeButton component
   */
  actionButton?: React.ReactElement<ButtonProps, typeof ArcadeButton>
}
const VARIANTS: Record<
  ArcadeStatusBannerVariant,
  {
    background: SemanticColors
    backgroundOpacity?: number
    border?: SemanticColors
    header: SemanticColors
    text: SemanticColors
  }
> = {
  prominent: {
    background: 'backgroundSubtle',
    header: 'textStandard',
    text: 'textSubtle',
  },
  subtle: {
    background: 'backgroundApp',
    border: 'borderSubtle',
    header: 'textStandard',
    text: 'textSubtle',
  },
  error: {
    background: 'backgroundDanger',
    backgroundOpacity: 0.1,
    header: 'textDanger',
    text: 'textDanger',
  },
  warning: {
    background: 'backgroundWarning',
    backgroundOpacity: 0.1,
    header: 'textWarning',
    text: 'textWarning',
  },
}

const StyledBanner = styled(Flex)<{ variant: ArcadeStatusBannerVariant }>`
  padding: ${spacing.m} ${spacing.smallGap};
  border-radius: ${({ theme }) => theme.border.radius.medium};
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;

  ${({ theme, variant }) => {
    const variantColors = VARIANTS[variant]
    const color = theme.arcadeColors.semantic[variantColors.text]
    const bgColor = theme.arcadeColors.semantic[variantColors.background]
    const background = variantColors.backgroundOpacity ? addOpacity(bgColor, variantColors.backgroundOpacity) : bgColor
    const border = variantColors.border
      ? `border: ${theme.pxToRem(1)} solid ${theme.arcadeColors.semantic[variantColors.border]};`
      : ''

    return `
      background: ${background};
      ${border}
      color: ${color};
    `
  }}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: flex-start;
  }
`

const IconContainer = styled.div`
  margin-top: ${spacing.xxs};
`

const CloseButton = styled.button<{ variant: ArcadeStatusBannerVariant }>`
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxs};
  color: ${({ theme, variant }) => theme.arcadeColors.semantic[VARIANTS[variant].header]};
  -webkit-tap-highlight-color: transparent;
  width: ${({ theme }) => theme.pxToRem(20)};
  height: ${({ theme }) => theme.pxToRem(20)};
  margin-top: ${({ theme }) => theme.pxToRem(2)};

  ${props => props.theme.selectors.keyboard} &:focus {
    outline: 1px solid;
  }
`

const HeaderIconContainer = styled.div<{ variant: ArcadeStatusBannerVariant }>`
  color: ${({ theme, variant }) => theme.arcadeColors.semantic[VARIANTS[variant].header]};
`

const TitleContainer = styled(Flex)<{ variant: ArcadeStatusBannerVariant }>`
  ${({ theme }) => theme.typography.labelMedium};
  color: ${({ theme, variant }) => theme.arcadeColors.semantic[VARIANTS[variant].header]};
`

const MessageContainer = styled.div<{ variant: ArcadeStatusBannerVariant }>`
  * {
    ${({ theme }) => theme.typography.body};
    color: ${({ theme, variant }) => theme.arcadeColors.semantic[VARIANTS[variant].text]};
  }
`

const ContentWrapper = styled(Flex)`
  flex: 1;
  flex-flow: row wrap;

  ${breakpoints.tablet} {
    flex-flow: row nowrap;
  }
`

const ArcadeStatusBanner: React.FC<ArcadeStatusBannerProps> = ({
  variant,
  title,
  hideTitleIcon,
  message,
  headerIcon,
  onClose,
  actionButton,
}) => {
  const defaultIcon = <InformationOutlineIcon size={16} data-testid="title-icon" />

  return (
    <StyledBanner variant={variant} data-testid={`${variant}-status-banner`}>
      {headerIcon && <HeaderIconContainer variant={variant}>{headerIcon}</HeaderIconContainer>}
      <Flex gap={spacing.xs}>
        {!hideTitleIcon && !headerIcon && <IconContainer>{defaultIcon}</IconContainer>}
        <ContentWrapper justifyContent="space-between" alignItems="center" gap={spacing.m}>
          <Stack space={spacing.xxs}>
            <TitleContainer variant={variant} alignItems="center" gap={spacing.xs}>
              {title}
            </TitleContainer>
            {message && <MessageContainer variant={variant}>{message}</MessageContainer>}
          </Stack>
          {actionButton && (
            <Flex flexShrink={0} alignItems="flex-start" gap={spacing.xs}>
              {actionButton}
            </Flex>
          )}
        </ContentWrapper>
        {onClose && (
          <CloseButton onClick={onClose} aria-label="Close" variant={variant}>
            <NavigationCloseIcon />
          </CloseButton>
        )}
      </Flex>
    </StyledBanner>
  )
}

export default ArcadeStatusBanner
